import { atom } from "jotai";
import { atomWithImmer } from "jotai-immer";

import { formAtom } from "@frontend/lib/form";
import { ApiError } from "@frontend/lib/http";
import { loadReferences } from "@frontend/lib/references";

import { clientSchema } from "../schemas/client";
import type { ClientValues } from "../schemas/client";
import { clientApi } from "../services";
import type { Client, ClientOptions, Contact, QuickviewProject } from "../types";
import { toClientValues } from "../utils/payload";

export type ClientEditorProjects = {
  subscriptions: QuickviewProject[];
  interventions: QuickviewProject[];
};

export type ClientEditorState = {
  clientId: number | null;
  client: Client | null;
  contact: Contact | null;
  projects: ClientEditorProjects;
  projectsError: string | null;
  options: ClientOptions;
  loading: boolean;
  saving: boolean;
  conflict: boolean;
  error: string | null;
};

const emptyOptions: ClientOptions = {
  titles: [],
  statuses: [],
  types: [],
  provenances: [],
  fields: [],
  regions: [],
  contactTypes: [],
  accountantTypes: [],
};

const emptyProjects = (): ClientEditorProjects => ({
  subscriptions: [],
  interventions: [],
});

export const emptyClient = (): ClientValues => ({
  lockVersion: null,
  contactId: null,
  title: null,
  firstName: null,
  lastName: null,
  isOrganization: false,
  organization: null,
  organizationSubName: null,
  phones: [],
  emails: [],
  urls: [],
  addresses: [],
  statusId: null,
  typeId: null,
  fieldId: null,
  regionId: null,
  provenanceId: null,
  provenanceNote: null,
  provenanceClientId: null,
  provenanceEmployeeId: null,
  comments: null,
  contacts: [],
  accountants: [],
});

export const initialClientEditorState: ClientEditorState = {
  clientId: null,
  client: null,
  contact: null,
  projects: emptyProjects(),
  projectsError: null,
  options: emptyOptions,
  loading: true,
  saving: false,
  conflict: false,
  error: null,
};

export const clientForm = formAtom(clientSchema, emptyClient(), {
  normalizeNull: false,
});

export const clientEditorAtom = atomWithImmer<ClientEditorState>(
  initialClientEditorState,
);

export type ClientEditorTab =
  | "main"
  | "contact"
  | "projects"
  | "notes"
  | "conditions";

export const editorTabAtom = atom<ClientEditorTab>("main");

const CONTACT_PATHS = /^(?:phones|emails|urls|addresses)(?:\.|$)/u;

export function tabForPath(path: string): ClientEditorTab {
  if (CONTACT_PATHS.test(path)) {
    return "contact";
  }

  return "comments" === path ? "notes" : "main";
}

export const revealErrorAtom = atom(
  null,
  (_get, set, paths: string[]): void => {
    const [first] = paths;

    if (undefined !== first) {
      set(editorTabAtom, tabForPath(first));
    }
  },
);

export const optionsAtom = atom((get) => get(clientEditorAtom).options);

export const clientAtom = atom((get) => get(clientEditorAtom).client);

export const reloadClientAtom = atom(
  null,
  async (_get, set, clientId: number): Promise<void> => {
    const client = await clientApi.fetchClient(clientId);

    set(clientEditorAtom, (state) => {
      state.client = client;
    });
  },
);

export const loadClientEditorAtom = atom(
  null,
  async (_get, set, clientId: number | null): Promise<void> => {
    set(editorTabAtom, "main");

    set(clientEditorAtom, (state) => {
      state.clientId = clientId;
      state.projects = emptyProjects();
      state.projectsError = null;
      state.loading = true;
      state.error = null;
      state.conflict = false;
    });

    try {
      const [options, client, projectsResult] = await Promise.all([
        loadClientOptions(),
        null === clientId ? null : clientApi.fetchClient(clientId),
        loadClientProjects(clientId),
      ]);

      const contactId = client?.contactId ?? null;

      const contact =
        null === contactId ? null : await clientApi.fetchContact(contactId);

      set(
        clientForm.actions.setInitial,
        null === client ? emptyClient() : toClientValues(client, contact),
      );

      set(clientEditorAtom, (state) => {
        state.options = options;
        state.client = client;
        state.contact = contact;
        state.projects = projectsResult.projects;
        state.projectsError = projectsResult.error;
        state.loading = false;
      });
    } catch (error: unknown) {
      set(clientEditorAtom, (state) => {
        state.loading = false;
        state.error = message(error);
      });
    }
  },
);

export const saveClientAtom = atom(
  null,
  async (get, set): Promise<number | null> => {
    const { clientId, saving } = get(clientEditorAtom);

    if (saving) {
      return null;
    }

    set(clientEditorAtom, (state) => {
      state.error = null;
      state.conflict = false;
      state.saving = true;
    });

    let savedId: number | null = null;

    try {
      const result = await set(clientForm.actions.submit, async (values) => {
        const saved =
          null === clientId
            ? await clientApi.createClient(values)
            : await clientApi.updateClient(clientId, values);

        savedId = saved.id;

        set(clientEditorAtom, (state) => {
          state.client = saved;
          state.clientId = saved.id;
        });

        set(clientForm.fields.lockVersion.atom, saved.lockVersion);
        set(clientForm.fields.contactId.atom, saved.contactId);

        const savedContactId = saved.contactId;

        const contact =
          null === savedContactId
            ? null
            : await clientApi.fetchContact(savedContactId);

        set(clientForm.actions.setInitial, toClientValues(saved, contact));

        set(clientEditorAtom, (state) => {
          state.contact = contact;
          state.clientId = saved.id;
          state.projects = null === clientId ? emptyProjects() : state.projects;
        });
      });

      if (!result.success) {
        set(revealErrorAtom, Object.keys(result.errors));

        return null;
      }
    } catch (error: unknown) {
      set(clientEditorAtom, (state) => {
        state.error = message(error);
        state.conflict = error instanceof ApiError && 409 === error.status;
      });

      if (error instanceof ApiError && 0 < error.violations.length) {
        set(clientForm.actions.setErrors, error.violations);

        set(
          revealErrorAtom,
          error.violations.map(({ path }) => path),
        );
      }

      return null;
    } finally {
      set(clientEditorAtom, (state) => {
        state.saving = false;
      });
    }

    return savedId;
  },
);

async function loadClientProjects(
  clientId: number | null,
): Promise<{ projects: ClientEditorProjects; error: string | null }> {
  if (null === clientId) {
    return { projects: emptyProjects(), error: null };
  }

  try {
    const quickview = await clientApi.fetchQuickview(clientId);

    return {
      projects: {
        subscriptions: quickview.subscriptions,
        interventions: quickview.interventions,
      },
      error: null,
    };
  } catch (error: unknown) {
    return { projects: emptyProjects(), error: message(error) };
  }
}

async function loadClientOptions(): Promise<ClientOptions> {
  const [
    titles,
    statuses,
    types,
    provenances,
    fields,
    regions,
    contactTypes,
    accountantTypes,
  ] = await Promise.all([
    loadReferences("contactTitles"),
    loadReferences("clientStatuses"),
    clientApi.fetchClientTypes(),
    clientApi.fetchProvenances(),
    loadReferences("clientFields"),
    loadReferences("clientRegions"),
    loadReferences("clientContactTypes"),
    loadReferences("clientAccountantTypes"),
  ]);

  return {
    titles: titles.map(({ label }) => label),
    statuses,
    types,
    provenances,
    fields,
    regions,
    contactTypes,
    accountantTypes,
  };
}

function message(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
